import React, { useState } from "react";
import Button from "../UI/Buttons/Button/Button";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import styles from "./PortalLogoutButton.module.scss";

// The one logout control of the dobrocudan.rs portal personas (advisor and
// association), used in the nav drawer and on the account panel. The persona
// hook decides the gates; this component only renders them: disabled during a
// live call, a confirm before discarding an unfinished consult draft,
// otherwise the hook's logout.

/** What a persona logout hook returns; `useAdvisorLogout` and `useAssociationLogout` both fit. */
export interface PortalLogoutControl {
  logout: () => void | Promise<void>;
  loggingOut: boolean;
  blockedByConsult?: boolean;
  hasUnfinishedDraft?: boolean;
}

export interface PortalLogoutButtonProps {
  control: PortalLogoutControl;
  className?: string;
  style?: React.CSSProperties;
  /** Fired right before the logout starts (the drawer closes itself). */
  onBeforeLogout?: () => void;
}

const TITLE_ID = "portal-logout-discard-title";

const PortalLogoutButton: React.FC<PortalLogoutButtonProps> = ({
  control,
  className,
  style,
  onBeforeLogout,
}) => {
  const { logout, loggingOut, blockedByConsult = false, hasUnfinishedDraft = false } =
    control;
  const [confirming, setConfirming] = useState(false);

  const perform = () => {
    onBeforeLogout?.();
    void logout();
  };

  const request = () => {
    if (blockedByConsult) {
      return; // the control is disabled; belt and braces
    }
    if (hasUnfinishedDraft) {
      setConfirming(true);
      return;
    }
    perform();
  };

  return (
    <>
      <Button
        className={className}
        style={style}
        buttonType="white"
        disabled={loggingOut || blockedByConsult}
        title={
          blockedByConsult
            ? "Odjava nije moguća tokom konsultacije."
            : undefined
        }
        onClick={request}
      >
        {loggingOut ? "Odjavljivanje..." : "Odjavi se"}
      </Button>
      <AccessibleDialog
        isOpen={confirming}
        onRequestClose={() => setConfirming(false)}
        titleId={TITLE_ID}
        maxWidth={480}
      >
        <div className={styles.dialog}>
          <h3 id={TITLE_ID} className={styles.title}>
            Nezavršena rezervacija
          </h3>
          <p>
            Imate nezavršenu rezervaciju ili RFZO upit iz konsultacije. Odjavom
            će nezavršeni unos biti odbačen.
          </p>
          <div className={styles.actions}>
            <Button buttonType="white" onClick={() => setConfirming(false)}>
              Odustani
            </Button>
            <Button
              buttonType="red"
              onClick={() => {
                setConfirming(false);
                perform();
              }}
            >
              Odjavi se
            </Button>
          </div>
        </div>
      </AccessibleDialog>
    </>
  );
};

export default PortalLogoutButton;
